import { Box, Stack, Typography } from "@mui/material";
import { Banner, FoodsDeleteButton, FoodsIconButton } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

import type { TransportLeg } from "src/api/client-api/transport/useTransportLegs";
import type { TransportModeChoice } from "src/api/sure-api/useSearchTransportModes";
import { AddItemField } from "src/components/EntityForm";
import { TransportResourceCard } from "src/components/Transport/TransportResourceCard";
import { getTransportLegModeIcons } from "src/utils/transport/modeIcons";
import { getTransportLegMeta } from "src/utils/transport/resourceMeta";

import { TransportCreateSection } from "./TransportCreatePageShell";
import { useTransportAppearance } from "./transportAppearance";

type SelectedLegEntry = {
    draftIndex: number;
    leg: TransportLeg | null;
    legId: number;
};

type TransportCircuitLegsSectionProps = {
    draftLegCount: number;
    editSectionTotal: number;
    isEditMode: boolean;
    isReferenceLocked: boolean;
    isTransportLegCatalogUnavailable: boolean;
    legCatalogStatus: ReactNode;
    selectedLegEntries: SelectedLegEntry[];
    transportModes: TransportModeChoice[];
    onAddLeg: () => void;
    onMoveLeg: (index: number, direction: -1 | 1) => void;
    onRemoveLeg: (index: number) => void;
};

export const TransportCircuitLegsSection = ({
    draftLegCount,
    editSectionTotal,
    isEditMode,
    isReferenceLocked,
    isTransportLegCatalogUnavailable,
    legCatalogStatus,
    selectedLegEntries,
    transportModes,
    onAddLeg,
    onMoveLeg,
    onRemoveLeg,
}: TransportCircuitLegsSectionProps) => {
    const { t } = useTranslation();
    const appearance = useTransportAppearance("product-v2");

    if (isReferenceLocked) return null;

    return (
        <TransportCreateSection
            title={t("transport.create.sections.itinerary_legs", "Legs")}
            progress={
                appearance === "legacy" && isEditMode ? undefined
                : isEditMode ?
                    { current: 2, total: editSectionTotal }
                :   { current: 3, total: 3 }
            }
            description={t("transport.create.itinerary_legs_help", "Legs that compose your itinerary.")}
        >
            {legCatalogStatus}
            {appearance === "legacy" && selectedLegEntries.length === 0 && !isTransportLegCatalogUnavailable ?
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    gap={1.5}
                    sx={(theme) => ({
                        borderColor: theme.custom.grey[700],
                        borderStyle: "dashed",
                        borderWidth: theme.custom.strokes.containerDivider.width,
                        borderRadius: theme.spacing(1),
                        backgroundColor: theme.custom.grey[200],
                        minHeight: theme.spacing(14),
                        p: 2,
                    })}
                >
                    <Typography variant="body2" sx={(theme) => ({ color: theme.custom.grey[2000] })}>
                        {t("transport.create.add_first_leg", "Add a leg to your itinerary.")}
                    </Typography>
                    <FoodsIconButton
                        size="m"
                        icon="plus"
                        tooltip={{ text: t("transport.circuits.add_leg", "Add leg") }}
                        buttonProps={{ onClick: onAddLeg }}
                    />
                </Stack>
            : selectedLegEntries.length === 0 && !isTransportLegCatalogUnavailable ?
                <AddItemField
                    actionLabel={t("transport.circuits.add_leg", "Add leg")}
                    description={t("transport.create.add_first_leg", "Add a leg to your itinerary.")}
                    onAction={onAddLeg}
                />
            : appearance === "legacy" && selectedLegEntries.length > 0 ?
                <Stack gap={1.25}>
                    {selectedLegEntries.map(({ draftIndex, leg, legId }) => {
                        const fallbackTitle = `${t("transport.resource.kind.leg", "Leg")} #${legId}`;

                        if (leg === null) {
                            return (
                                <Stack
                                    key={`unresolved-${draftIndex}-${legId}`}
                                    alignItems="center"
                                    direction="row"
                                    gap={1}
                                    role="alert"
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Banner variant="error">
                                            {fallbackTitle} — {t("common.not_available", "Not available")}
                                        </Banner>
                                    </Box>
                                    <FoodsIconButton
                                        size="s"
                                        icon="delete"
                                        tooltip={{ text: t("common.action.delete", "Delete") }}
                                        buttonProps={{
                                            "aria-label": `${t("common.action.delete", "Delete")} ${fallbackTitle}`,
                                            onClick: () => onRemoveLeg(draftIndex),
                                            type: "button",
                                        }}
                                    />
                                </Stack>
                            );
                        }

                        const title = leg.name ?? fallbackTitle;

                        return (
                            <TransportResourceCard
                                key={`${draftIndex}-${leg.id}`}
                                appearance="legacy"
                                kind="leg"
                                title={title}
                                subtitle={`${leg.originLabel ?? leg.originCountryCode ?? "-"} -> ${
                                    leg.destinationLabel ?? leg.destinationCountryCode ?? "-"
                                }`}
                                modeIcons={getTransportLegModeIcons(leg, transportModes)}
                                selectable={false}
                                meta={getTransportLegMeta(leg, t)}
                                actions={
                                    <>
                                        <FoodsIconButton
                                            size="s"
                                            icon="arrowUp"
                                            buttonProps={{
                                                onClick: () => onMoveLeg(draftIndex, -1),
                                                disabled: draftIndex === 0,
                                                "aria-label": `${title} ↑`,
                                            }}
                                        />
                                        <FoodsIconButton
                                            size="s"
                                            icon="arrowDown"
                                            buttonProps={{
                                                onClick: () => onMoveLeg(draftIndex, 1),
                                                disabled: draftIndex === draftLegCount - 1,
                                                "aria-label": `${title} ↓`,
                                            }}
                                        />
                                        <FoodsIconButton
                                            size="s"
                                            icon="delete"
                                            tooltip={{ text: t("common.action.delete", "Delete") }}
                                            buttonProps={{
                                                "aria-label": `${t("common.action.delete", "Delete")} ${title}`,
                                                onClick: () => onRemoveLeg(draftIndex),
                                                type: "button",
                                            }}
                                        />
                                    </>
                                }
                            />
                        );
                    })}
                    <FoodsIconButton
                        size="m"
                        icon="plus"
                        tooltip={{ text: t("transport.circuits.add_leg", "Add leg") }}
                        buttonProps={{ onClick: onAddLeg, disabled: isTransportLegCatalogUnavailable }}
                    />
                </Stack>
            : selectedLegEntries.length > 0 ?
                <Stack gap={1.25}>
                    {selectedLegEntries.map(({ draftIndex, leg, legId }) => {
                        const fallbackTitle = `${t("transport.resource.kind.leg", "Leg")} #${legId}`;

                        if (leg === null) {
                            return (
                                <Stack
                                    key={`unresolved-${draftIndex}-${legId}`}
                                    alignItems="center"
                                    direction="row"
                                    gap={1}
                                    role="alert"
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Banner variant="error">
                                            {fallbackTitle} — {t("common.not_available", "Not available")}
                                        </Banner>
                                    </Box>
                                    <FoodsDeleteButton
                                        ariaLabel={`${t("common.action.delete", "Delete")} ${fallbackTitle}`}
                                        onClick={() => onRemoveLeg(draftIndex)}
                                        tooltip={{ text: t("common.action.delete", "Delete") }}
                                        buttonProps={{ type: "button" }}
                                    />
                                </Stack>
                            );
                        }

                        const title = leg.name ?? fallbackTitle;

                        return (
                            <TransportResourceCard
                                key={`${draftIndex}-${leg.id}`}
                                appearance={appearance}
                                kind="leg"
                                title={title}
                                subtitle={`${leg.originLabel ?? leg.originCountryCode ?? "-"} -> ${
                                    leg.destinationLabel ?? leg.destinationCountryCode ?? "-"
                                }`}
                                modeIcons={getTransportLegModeIcons(leg, transportModes)}
                                selectable={false}
                                meta={getTransportLegMeta(leg, t)}
                                actions={
                                    <>
                                        <FoodsIconButton
                                            size="s"
                                            icon="arrowUp"
                                            tooltip={{ text: `${title} ↑` }}
                                            buttonProps={{
                                                onClick: () => onMoveLeg(draftIndex, -1),
                                                disabled: draftIndex === 0,
                                                "aria-label": `${title} ↑`,
                                            }}
                                        />
                                        <FoodsIconButton
                                            size="s"
                                            icon="arrowDown"
                                            tooltip={{ text: `${title} ↓` }}
                                            buttonProps={{
                                                onClick: () => onMoveLeg(draftIndex, 1),
                                                disabled: draftIndex === draftLegCount - 1,
                                                "aria-label": `${title} ↓`,
                                            }}
                                        />
                                        <FoodsDeleteButton
                                            ariaLabel={`${t("common.action.delete", "Delete")} ${title}`}
                                            onClick={() => onRemoveLeg(draftIndex)}
                                            tooltip={{ text: t("common.action.delete", "Delete") }}
                                            buttonProps={{ type: "button" }}
                                        />
                                    </>
                                }
                            />
                        );
                    })}
                    <FoodsIconButton
                        size="m"
                        icon="plus"
                        tooltip={{ text: t("transport.circuits.add_leg", "Add leg") }}
                        buttonProps={{ onClick: onAddLeg, disabled: isTransportLegCatalogUnavailable }}
                    />
                </Stack>
            :   null}
        </TransportCreateSection>
    );
};
